/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable } from '@angular/core';
import {
  API_CONFIG,
  BaseCrudService,
  buildSearchByTerm,
  PageResponse,
} from '@nebula-shared/data-access';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PageRequest, TerceroResponse } from '../../domain/maestro-terceros-modal.models';

@Injectable({ providedIn: 'root' })
export class TercerosService extends BaseCrudService<TerceroResponse> {
  constructor() {
    super();
    const config = API_CONFIG.accounting;
    this.setBaseUrl(`${config.baseUrl}/terceros`);
  }
  getTercerosPaginados(
    page = 0,
    size = 10,
    searchTerm = '',
    soloActivos = true,
    tipoTercero?: string,
    terceroIdentificacion?: string,
  ): Observable<PageResponse<TerceroResponse>> {
    const searchsBy = [];

    if (soloActivos) {
      searchsBy.push({
        key: 'estado',
        operation: 'MATCH',
        value: 'ACTIVO',
        linkOperator: 'AND',
      });
    }

    if (searchTerm && searchTerm.trim() !== '') {
      searchsBy.push(
        {
          key: 'numeroDocumento',
          operation: 'MATCH',
          value: searchTerm,
          linkOperator: 'OR',
        },
        {
          key: 'nombreCompleto',
          operation: 'MATCH',
          value: searchTerm,
        },
      );
    }

    const body: PageRequest = {
      page: page.toString(),
      size: size.toString(),
      searchsBy: searchsBy,
      ordersBy: [{ key: 'numeroDocumento', direction: 'ASC' }],
    };

    return this.http.post<any>(`${this.baseUrl}/page-response`, body).pipe(
      map((response) => {
        const data = response?.response?.content || response;
        return {
          content: data?.content || [],
          totalElements: data?.totalElements || 0,
          totalPages: data?.totalPages || 0,
          size: data?.size || size,
          number: data?.number || page,
          numberOfElements: data?.content?.length || 0,
          first: page === 0,
          last: page >= (data?.totalPages || 1) - 1,
          empty: data?.content?.length === 0,
        } as PageResponse<TerceroResponse>;
      }),
    );
  }

  buscarPorDocumento(numeroDocumento: string): Observable<TerceroResponse | null> {
    const documentoLimpio = numeroDocumento.split('-')[0].trim();

    return this.getTercerosPaginados(0, 1, '', true, undefined, documentoLimpio).pipe(
      map((response) => {
        return response.content.length > 0 ? response.content[0] : null;
      }),
    );
  }

  buscarProveedorPorDocumento(numeroDocumento: string): Observable<TerceroResponse | null> {
    const documentoLimpio = numeroDocumento.split('-')[0].trim();
    return this.getTercerosPaginados(0, 1, '', true, 'PROVEEDOR', documentoLimpio).pipe(
      map((response) => {
        return response.content.length > 0 ? response.content[0] : null;
      }),
    );
  }

  buscarClientePorDocumento(numeroDocumento: string): Observable<TerceroResponse | null> {
    const documentoLimpio = numeroDocumento.split('-')[0].trim();
    return this.getTercerosPaginados(0, 1, '', true, 'CLIENTE', documentoLimpio).pipe(
      map((response) => {
        return response.content.length > 0 ? response.content[0] : null;
      }),
    );
  }

  getAllTerceros(soloActivos = true, tipoTercero?: string): Observable<TerceroResponse[]> {
    return this.getTercerosPaginados(0, 100, '', soloActivos, tipoTercero).pipe(
      map((firstPage) => {
        const allContent = [...firstPage.content];
        return allContent;
      }),
    );
  }
  getTercerosConOpPaginados(
    page = 0,
    size = 10,
    searchTerm = '',
    sortColumn?: string,
    sortDirection: 'ASC' | 'DESC' = 'ASC',
  ): Observable<PageResponse<TerceroResponse>> {
    const searchsBy = buildSearchByTerm(searchTerm, [
      { key: 'terceroNombre', type: 'text' },
      { key: 'terceroIdentificación', type: 'text' },
    ]);

    const ordersBy: { column: string; direction: 'ASC' | 'DESC' }[] = [];
    if (sortColumn) {
      ordersBy.push({ column: sortColumn, direction: sortDirection });
    }

    const filters = {
      page,
      size,
      ...(searchsBy.length > 0 && { searchsBy }),
      ...(ordersBy.length > 0 && { ordersBy }),
    };

    return this.http.post<any>(`${this.baseUrl}/ordenes-pago/aprobadas-o-con-saldo`, filters).pipe(
      map((response) => {
        const data = response?.response?.content || response;
        return {
          content: data?.content || [],
          totalElements: data?.totalElements || 0,
          totalPages: data?.totalPages || 0,
          size: data?.size || size,
          number: data?.number || page,
          numberOfElements: data?.content?.length || 0,
          first: page === 0,
          last: page >= (data?.totalPages || 1) - 1,
          empty: data?.content?.length === 0,
        } as PageResponse<TerceroResponse>;
      }),
    );
  }
}
